import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useFlags } from 'launchdarkly-react-client-sdk'

import Button from 'common/components/button/Button'
import ConfirmationModal from 'common/components/confirmationModal/ConfirmationModal'
import Drawer from 'common/components/drawer/Drawer'
import FormItem from 'common/components/FormItem'
import Input from 'common/components/Input'
import SingleSelect, {
  SelectValue,
  SingleValue,
} from 'common/components/singleSelect'
import { errorToast, successToast } from 'common/components/toastNotification'
import { callbackOAuth } from 'features/auth/store/authSlice'

import { Account, DashboardMode } from '../../Accounts'
import api from '../../api'
import LogoUploader from '../logoUploader'

export type AccountsDrawerProps = {
  mode: 'edit' | 'create'
  isOpen: boolean
  account?: Account
  onClose: (shouldRefetch?: boolean) => void
}

const options: SelectValue[] = [
  { value: 'Education', label: 'Education' },
  {
    value: 'Business & Marketing Services',
    label: 'Business & Marketing Services',
  },
  { value: 'Automotive', label: 'Automotive' },
  {
    value: 'Computers, Technology & SaaS',
    label: 'Computers, Technology & SaaS',
  },
  {
    value: 'Non-Profit, Fund Raising & Politics',
    label: 'Non-Profit, Fund Raising & Politics',
  },
  {
    value: 'Ecommerce Site/Retail Store',
    label: 'Ecommerce Site/Retail Store',
  },
  { value: 'Financial Services', label: 'Financial Services' },
  { value: 'Healthcare', label: 'Healthcare' },
  {
    value: 'Media, Publishing & Paid Content',
    label: 'Media, Publishing & Paid Content',
  },
  { value: 'Travel & Hospitality', label: 'Travel & Hospitality' },
  { value: 'Other', label: 'Other' },
]

const accountCategories: SelectValue[] = [
  {
    value: 'Starter',
    label: 'Starter',
  },
  {
    value: 'Pro',
    label: 'Pro',
  },
  {
    value: 'Enterprise',
    label: 'Enterprise',
  },
]

const dashboardModes: SelectValue[] = [
  {
    value: 'Average_uplift',
    label: 'Average uplift',
  },
  {
    value: 'Incremental',
    label: 'Incremental',
  },
]

const urlToFileObject = async (imageUrl: string): Promise<File | undefined> => {
  try {
    const response = await fetch(imageUrl, {
      headers: { 'Cache-Control': 'no-cache' },
    })

    const blob = await response.blob()
    const file = new File([blob], 'image.jpg', { type: blob.type })

    return file
  } catch (error) {
    errorToast('Something went wrong. Please try again.')
    return
  }
}

const AccountsDrawer = ({
  isOpen,
  mode,
  account,
  onClose,
}: AccountsDrawerProps) => {
  const [hasError, setHasError] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState(false)
  const [logo, setLogo] = useState<File | undefined>()
  const [accountName, setAccountName] = useState('')
  const [industry, setIndustry] = useState<string | undefined>()
  const [accountCategory, setAccountCategory] = useState<string | undefined>()
  const [dashboardMode, setDashboardMode] =
    useState<DashboardMode | undefined>()
  const [selectedAccountCategory, setSelectedAccountCategory] =
    useState<string | undefined>()
  const [isChangeCategoryModalShown, setIsChangeCategoryModalShown] =
    useState<boolean>(false)

  const flags = useFlags()
  const dispatch = useDispatch()

  const isEditMode = mode === 'edit'
  const title = isEditMode ? 'Edit Account' : 'Create Account'
  const areAllFieldsFilled = flags.showAccountLogo
    ? !!(accountName && industry && accountCategory && dashboardMode && logo)
    : !!(accountName && industry && accountCategory && dashboardMode)

  const onCloseOrCancel = (shouldRefetch?: boolean) => {
    setAccountName('')
    setLogo(undefined)
    setIndustry(undefined)
    setAccountCategory(undefined)
    setDashboardMode(undefined)
    setHasError(false)
    onClose(shouldRefetch)
  }

  const createOrUpdateAccount = async (accountId?: string) => {
    if (accountName && industry && accountCategory && dashboardMode && logo) {
      try {
        setIsLoading(true)
        setHasError(false)

        const result = await api.createOrUpdateAccountWithLogo({
          accountName,
          industry,
          logo,
          accountId,
          accountCategory,
          dashboardMode,
        })

        successToast(
          `Account ${result.companyName} ${isEditMode ? 'updated' : 'created'}`
        )
        dispatch(callbackOAuth(true))
        onCloseOrCancel(true)
      } catch {
        setHasError(true)
      } finally {
        setIsLoading(false)
      }
    } else if (accountName && industry && accountCategory && dashboardMode) {
      try {
        setIsLoading(true)
        setHasError(false)

        const result = await api.createOrUpdateAccountWithoutLogo({
          accountName,
          industry,
          accountId,
          accountCategory,
          dashboardMode,
        })

        successToast(
          `Account ${result.companyName} ${isEditMode ? 'updated' : 'created'}`
        )
        dispatch(callbackOAuth(true))
        onCloseOrCancel(true)
      } catch {
        setHasError(true)
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleCategoryChange = (val: SingleValue<SelectValue>) => {
    if (isEditMode) {
      setSelectedAccountCategory(val?.value)
      setIsChangeCategoryModalShown(true)
    } else {
      setAccountCategory(val?.value)
    }
  }

  useEffect(() => {
    if (isEditMode && account && isOpen) {
      const loadFields = async () => {
        setAccountName(account.companyName)
        setIndustry(account.industry)
        setAccountCategory(account.accountCategory)
        setDashboardMode(account.dashboardMode)

        if (flags.showAccountLogo) {
          const logoObject = await urlToFileObject(account.logoUrl!)
          setLogo(logoObject)
        }
      }
      loadFields()
    }
  }, [account, isEditMode, isOpen, flags.showAccountLogo])

  const content = (
    <>
      {flags.showAccountLogo && (
        <div className="my-8 h-40">
          <LogoUploader
            logo={logo}
            onClear={() => setLogo(undefined)}
            onDrop={(file) => setLogo(file)}
          />
        </div>
      )}

      <FormItem label="Account name">
        <Input
          aria-label="Account name"
          type="text"
          variant="default"
          value={accountName}
          onChange={(event) => setAccountName(event.target.value)}
        />
      </FormItem>
      <FormItem label="Industry">
        <SingleSelect
          aria-label="Industry"
          value={industry}
          onChange={(val) => setIndustry(val?.value)}
          options={options}
        />
      </FormItem>
      <FormItem label="Account category">
        <SingleSelect
          aria-label="Account category"
          value={accountCategory}
          onChange={handleCategoryChange}
          options={accountCategories}
        />
      </FormItem>
      <FormItem label="Dashboard mode">
        <SingleSelect
          aria-label="Dashboard mode"
          value={dashboardMode}
          onChange={(val) => setDashboardMode(val?.value as DashboardMode)}
          options={dashboardModes}
        />
      </FormItem>
    </>
  )

  const footer = (
    <div className="flex justify-end text-base">
      <Button
        ghost
        className="mr-4 text-coolGray-400"
        onClick={() => onCloseOrCancel()}
        data-cy="account-drawer-cancel-button"
      >
        Cancel
      </Button>
      {isEditMode ? (
        <Button
          variant="primary"
          onClick={() => createOrUpdateAccount(account?.id)}
          loading={isLoading}
          disabled={!areAllFieldsFilled}
          data-cy="edit-account-update-button"
        >
          Update
        </Button>
      ) : (
        <Button
          variant="primary"
          onClick={() => createOrUpdateAccount()}
          loading={isLoading}
          disabled={!areAllFieldsFilled}
          data-cy="add-account-save-button"
        >
          Save
        </Button>
      )}
    </div>
  )

  return (
    <>
      <Drawer
        content={content}
        footer={footer}
        error={hasError ? 'Something went wrong. Please try again.' : undefined}
        title={title}
        isOpen={isOpen}
        onClose={onCloseOrCancel}
      />
      <ConfirmationModal
        title="Change the category?"
        confirmationText="Are you sure you want to change the category? This could impact what the users see when using the app and what they have access to."
        confirmButtonText="Yes"
        cancelButtonText="Cancel"
        open={isChangeCategoryModalShown}
        onConfirm={() => {
          setAccountCategory(selectedAccountCategory)
          setIsChangeCategoryModalShown(false)
        }}
        onCancel={() => setIsChangeCategoryModalShown(false)}
      />
    </>
  )
}

export default AccountsDrawer
